import {
    FetchState,
    ListParams,
    MobileApp,
    MobileAppSearchRequest,
    MobileAppSearchResponse,
    MobileAppState,
} from "@/shared/types";
import {
    createMobileApp,
    searchMobileApps,
    updateMobileApp,
    deleteMobileApp
} from "@/shared/api/userService";
import moment from "moment";
import * as storeHelper from "@/shared/storeHelper";
import Vue from "vue";
import { defineStore } from "pinia";
import _ from "lodash";
import { usePageSizeStore } from "@/shared/store/pageSize";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: MobileAppState = {
    pageNumber: 1,
    totalRecords: 0,
    mobileAppPages: [],
    mobileApps: [],
    mobileAppFetchState: FetchState.Uninitialized
};

export const useMobileAppStore = defineStore("mobileApp", {
    state: (): MobileAppState => (_.cloneDeep(initialState)),
    getters: {
        mobileAppsLoaded: state => state.mobileAppFetchState === FetchState.Loaded,
        mobileAppsFetchLoading: state => state.mobileAppFetchState === FetchState.Loading,
        pageSize (): number {
            const pageSizeStore = usePageSizeStore();
            return pageSizeStore.pageSize;
        },
        pageCount (): number {
            const pageSizeStore = usePageSizeStore();
            const pageSize = pageSizeStore.pageSize;
            return this.totalRecords < pageSize ? 1 : Math.floor((this.totalRecords + pageSize - 1) / pageSize);
        },
        mobileAppById (): (mobileAppId: number | any) => MobileApp | null {
            return (mobileAppId: number | any) => {
                for (const mobileApp of this.mobileApps) {
                    if (mobileApp.mobileAppId === mobileAppId) {
                        return mobileApp;
                    }
                }
                return null;
            };
        },
        getMobileAppsByPageNumber (): (pageNumber: number | any) => any {
            return (pageNumber: number | any) => {
                const pageObject = this.mobileAppPages[pageNumber];
                if (typeof pageObject === "undefined") {
                    return [];
                }
                return pageObject;
            };
        },
    },
    actions: {
        clearMobileAppPages () {
            this.mobileAppPages = [];
            this.totalRecords = 0;
        },
        setMobileAppFetchState (fetchState: FetchState) {
            this.mobileAppFetchState = fetchState;
        },
        setMobileApps (mobileAppList: MobileAppSearchResponse) {
            for (const mobileApp of mobileAppList.mobileApps) {
                mobileApp.dateCreated = moment(mobileApp.dateCreated).format("MM/DD/YYYY");
                mobileApp.dateModified = moment(mobileApp.dateModified).format("MM/DD/YYYY");
            }

            // A new record was added by another system user so we need to refresh all the stored pages.
            if (this.totalRecords !== mobileAppList.totalRecords) {
                this.mobileApps = [];
            }

            const mobileAppPages = [ ...this.mobileAppPages ];
            mobileAppPages[mobileAppList.pageNumber] = mobileAppList.mobileApps;

            this.totalRecords = mobileAppList.totalRecords;
            this.pageNumber = mobileAppList.pageNumber;
            this.mobileAppPages = mobileAppPages;

            this.mobileApps = this.mobileApps.concat(mobileAppList.mobileApps);
        },
        setMobileApp (mobileApp: MobileApp) {
            mobileApp.dateCreated = moment(mobileApp.dateCreated).format("MM/DD/YYYY");
            mobileApp.dateModified = moment(mobileApp.dateModified).format("MM/DD/YYYY");

            const index = this.mobileApps.findIndex((e: MobileApp) => e.mobileAppId === mobileApp.mobileAppId);

            if (index >= 0) {
                Vue.set(this.mobileApps, index, mobileApp);
            } else {
                this.mobileApps = [ mobileApp, ...this.mobileApps ];
            }

            const mobileAppPages = [ ...this.mobileAppPages ];
            mobileAppPages[this.pageNumber] = this.mobileApps;
            this.mobileAppPages = mobileAppPages;
        },
        removeMobileApp (appId: number) {
            const mobileApps = this.mobileApps;
            const index = mobileApps.findIndex(app => app.mobileAppId === appId);

            if (index >= 0) {
                mobileApps.splice(index, 1);
                this.mobileApps = mobileApps;

                const mobileAppPages = [ ...this.mobileAppPages ];
                mobileAppPages[this.pageNumber] = this.mobileApps;
                this.mobileAppPages = mobileAppPages;
            }
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        search ({ clearState, pageNumber, searchTerm }: ListParams) {
            const pageSizeStore = usePageSizeStore();
            return new Promise<void>(resolve => {
                const pageSize = pageSizeStore.pageSize;
                const searchRequest: MobileAppSearchRequest = { pageNumber, pageSize, searchTerm };

                if (clearState) {
                    this.clearMobileAppPages();
                    this.setMobileAppFetchState(FetchState.Loading);
                    searchMobileApps(searchRequest)
                        .then(response => {
                            this.setMobileApps(response.data);
                            this.setMobileAppFetchState(FetchState.Loaded);
                            resolve();
                        }).catch(err => {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    });
                }

                const pageObject = this.mobileApps[pageNumber || 1];
                if (typeof pageObject !== "undefined") {
                    resolve();
                    return;
                } else {
                    this.setMobileAppFetchState(FetchState.Loading);
                    searchMobileApps(searchRequest)
                        .then(response => {
                            this.setMobileApps(response.data);
                            this.setMobileAppFetchState(FetchState.Loaded);
                            resolve();
                        }).catch(err => {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        async updateMobileApp (request: MobileApp) {
            const notifierStore = useNotifierStore();
            if (request.mobileAppId === 0) {
                return createMobileApp(request)
                    .then(response => {
                        this.setMobileApp(response.data);
                        notifierStore.setSuccess("Mobile application created");
                    })
                    .catch(error => storeHelper.handleError({ error, rethrow: true }));
            } else {
                return updateMobileApp(request)
                    .then(response => {
                        this.setMobileApp(response.data);
                        notifierStore.setSuccess("Mobile application updated");
                    })
                    .catch(error => storeHelper.handleError({ error, rethrow: true }));
            }
        },
        deleteMobileApp (appId: number) {
            deleteMobileApp(appId)
              .then(() => {
                  this.removeMobileApp(appId);
                  const notifier = useNotifierStore();
                  notifier.setSuccess("Mobile application removed");
              })
              .catch(error => storeHelper.handleError({ error, rethrow: false }));
        }
    }
});
